import { BrowserRouter } from "react-router-dom";
import { AppRoutes } from "./router";
import { I18nextProvider } from "react-i18next";
import i18n from "./i18n";
import { AuthProvider } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { ToastContainer } from "react-toastify";
import useAuthStatus from "./hooks/useAuthStatus";
import { useNotifications } from "./hooks/useNotifications";
import AnalyticsProvider from "./components/AnalyticsProvider";
import { setupNativePushBridge } from "./utils/registerNativePushToken";
import SupportNotificationsMount from "./components/SupportNotificationsMount";
import { useAuth } from "./context/useAuth";

// FIXED — ROOT CAUSE of "useAuth() called outside of <AuthProvider>":
// `App` was BOTH the component that DECLARES <AuthProvider> in its
// JSX, AND a component that tried to CONSUME it via useAuth() in its
// own function body. A provider only exists for components rendered
// BENEATH it in the tree — App's own body runs before App's return
// value (which contains <AuthProvider>) is ever rendered, so there
// was no AuthProvider ancestor yet at the moment useAuth() ran. This
// wasn't a "forgot to wrap it" bug (main.jsx's earlier fix wrapping
// <App/> in a SECOND, outer AuthProvider would have worked, but only
// by accident — papering over the real structural issue with a
// duplicate provider instead of fixing the actual placement).
//
// FIX: split into two components.
//   - App (this one) ONLY sets up providers (Theme, i18n, Router,
//     Analytics, Toast, Auth) — it does NOT call useAuth() itself,
//     since it's the one DEFINING the Auth boundary, not living
//     inside it.
//   - AppContent (below) renders INSIDE <AuthProvider>, and is where
//     useAuth(), useAuthStatus(), useNotifications(), and
//     SupportNotificationsMount now live — all genuinely beneath the
//     provider in the tree, so useAuth() resolves correctly.
//
// main.jsx does NOT need its own separate <AuthProvider> wrapper —
// this file is once again the single source of the Auth boundary,
// which is the correct shape. (If main.jsx was already changed to add
// a second AuthProvider around <App/>, that's now redundant — two
// nested AuthProviders both work functionally since the inner one
// just shadows the outer, but only one is needed; remove the outer
// one in main.jsx to keep a single source of truth.)
function App() {
  return (
    <ThemeProvider>
      <I18nextProvider i18n={i18n}>
        <BrowserRouter basename={__BASE_PATH__}>
          <AnalyticsProvider>
            <ToastContainer
              position="top-right"
              autoClose={4000}
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              pauseOnHover
              draggable
              theme="light"
            />
            <AuthProvider>
              <AppContent />
            </AuthProvider>
          </AnalyticsProvider>
        </BrowserRouter>
      </I18nextProvider>
    </ThemeProvider>
  );
}

// FIXED — new. Everything that needs useAuth() (directly or via
// hooks built on top of it) now lives here, genuinely rendered
// beneath <AuthProvider> in App's JSX above, instead of in App's own
// function body where no provider ancestor existed yet.
function AppContent() {
  const isAuthenticated = useAuthStatus();
  const { user } = useAuth();
  useNotifications(isAuthenticated);
  setupNativePushBridge();

  return (
    <>
      <SupportNotificationsMount user={user} />
      <AppRoutes />
    </>
  );
}

export default App;
